/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  DownCircleFilled,
  InfoCircleOutlined,
  SearchOutlined,
  FileExcelOutlined,
  ConsoleSqlOutlined,
  FileTextOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import {
  Space,
  Tag,
  Dropdown,
  Popconfirm,
  message,
  Tooltip,
  Switch,
} from "antd";
import moment from "moment";
import Link from "antd/es/typography/Link";
import "../styles/table.css";
import { createDefaultStandalone } from "../service/suite.service";
import COLORS from "../styles/colors";
import SearchFilter from "../components/generic/SearchFilter";
import ColorTags from "../components/generic/ColorTags";
import CategoryTypeCell from "../components/generic/CategoryTypeCell";
import {
  formatDisplayDate,
  formatDuration,
  parseDateString,
} from "../helper/date_helper";
import {
  labelForCategory,
  labelForSubCategory,
} from "./reconciliationLabels";

// Document Library — friendly labels for the stored document-type codes.
const DOCUMENT_TYPE_LABELS = { spec: "Specification", sql: "SQL Query" };

// Document Library — File Type column: icon + label per file extension.
const FILE_TYPE_META = {
  xlsx: { icon: <FileExcelOutlined />, color: "#3B6D11", label: "XLSX" },
  sql: { icon: <ConsoleSqlOutlined />, color: "#185FA5", label: "SQL" },
  txt: { icon: <FileTextOutlined />, color: "#6C8A9B", label: "TXT" },
};

export const db_columns = [
  {
    title: "DB Name",
    dataIndex: "dbName",
    key: "name",
    render: (text) => <a>{text}</a>,
  },
  {
    title: "DB Type",
    dataIndex: "dbType",
    key: "type",
  },
  {
    title: "Connection Details",
    dataIndex: "connectionString",
    key: "details",
  },
  {
    title: "Port",
    key: "port",
    dataIndex: "port",
  },
  {
    title: "Action",
    key: "action",
    render: (_, record) => (
      <Space size="middle">
        <a>Edit</a>
      </Space>
    ),
  },
];

export const file_spec_columns = (onSuiteClick, onView, onEdit, onVersions) => [
  {
    title: "Document Type",
    dataIndex: "documentType",
    key: "documentType",
    filters: [
      { text: "Specification", value: "spec" },
      { text: "SQL Query", value: "sql" },
    ],
    filterMode: "tree",
    sorter: (a, b) => a.documentType.localeCompare(b.documentType),
    onFilter: (value, record) => record.documentType === value,
    render: (value, record) => {
      const fileMeta = FILE_TYPE_META[record.documentFileType];
      return (
        <span className="arc-file-type">
          {fileMeta && (
            <span
              className="arc-file-type-icon"
              style={{ color: fileMeta.color }}
            >
              {fileMeta.icon}
            </span>
          )}
          {DOCUMENT_TYPE_LABELS[value] || value}
        </span>
      );
    },
    width: "12%",
  },
  {
    title: "Display Name",
    dataIndex: "specName",
    key: "name",
    width: "20%",
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <SearchFilter
        setSelectedKeys={setSelectedKeys}
        selectedKeys={selectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
      />
    ),
    filterIcon: () => <SearchOutlined style={{ color: "rgb(255,255,255)" }} />,
    onFilter: (value, record) =>
      record.specName.toLowerCase().includes(value.toLowerCase()),
    sorter: (a, b) => a.specName.localeCompare(b.specName),
    render: (text) => <span className="arc-library-name">{text}</span>,
  },
  {
    title: "File Name",
    dataIndex: "s3Path",
    key: "s3Path",
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <SearchFilter
        setSelectedKeys={setSelectedKeys}
        selectedKeys={selectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
      />
    ),
    filterIcon: () => <SearchOutlined style={{ color: "rgb(255,255,255)" }} />,
    onFilter: (value, record) =>
      record.s3Path.toLowerCase().includes(value.toLowerCase()),
    render: (text) => {
      // Extract filename from S3 path — strip timestamp prefix
      const filename = text
        .split("/")
        .pop()
        .substring(text.split("/").pop().indexOf("-") + 1);
      // href points to S3 — this is an external link, href is correct here
      return (
        <a href={text} target="_blank" rel="noreferrer">
          {filename}
        </a>
      );
    },
    width: "25%",
    sorter: (a, b) => a.s3Path.localeCompare(b.s3Path),
  },
  {
    title: "Category",
    dataIndex: "category",
    key: "category",
    sorter: (a, b) => a.category.localeCompare(b.category),
    width: "10%",
    render: (v) => labelForCategory(v),
  },
  {
    title: "Created By",
    dataIndex: "createdBy",
    key: "createdBy",
    width: "15%",
  },
  {
    title: "Created Date",
    dataIndex: "createDate",
    key: "createDate",
    width: "10%",
    defaultSortOrder: "descend",
    sorter: (a, b) =>
      parseDateString(a.createDate) - parseDateString(b.createDate),
    render: (v) => formatDisplayDate(v),
  },
  {
    title: "Comments",
    dataIndex: "comments",
    key: "comments",
    width: "20%",
    filterDropdown: ({
      selectedKeys,
      setSelectedKeys,
      confirm,
      clearFilters,
    }) => (
      <SearchFilter
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
      />
    ),
    filterIcon: () => <SearchOutlined style={{ color: "rgb(255,255,255)" }} />,
    onFilter: (value, record) =>
      record.comments.toLowerCase().includes(value.toLowerCase()),
    sorter: (a, b) => a.comments.localeCompare(b.comments),
  },
  {
    title: "Test Suites",
    dataIndex: "count",
    key: "testSuites",
    width: "10%",
    sorter: (a, b) => a.count - b.count,
    render: (_, record) => (
      <Link
        className="table-tests-count"
        onClick={(e) => {
          e.preventDefault();
          onSuiteClick?.(record);
        }}
      >
        {record.count}
      </Link>
    ),
  },
  {
    title: "Action",
    key: "action",
    width: "10%",
    render: (_, record) => (
      <Dropdown
        menu={{
          items: [
            ...(record.documentType === "spec"
              ? [
                  {
                    key: "0",
                    label: "View",
                    onClick: () => onView?.(record),
                  },
                ]
              : []),
            {
              key: "1",
              label: "Edit",
              onClick: () => onEdit?.(record),
            },
            ...(record.documentType === "spec"
              ? [
                  {
                    key: "4",
                    label: "Version History",
                    onClick: () => onVersions?.(record),
                  },
                ]
              : []),
            { key: "2", label: "Delete" },
            {
              key: "3",
              label: (
                <Popconfirm
                  title="Create default suite"
                  description="Are you sure you want to create a default test suite?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => {
                    createDefaultStandalone(record.specName).then((res) => {
                      message.success(
                        `Test suite ${res.data.message} created successfully`,
                      );
                    });
                  }}
                >
                  Create Test Suite
                </Popconfirm>
              ),
            },
          ],
        }}
        overlayClassName="table-row-actions-dropdown"
      >
        <a className="table-more-options">
          Options <DownCircleFilled />
        </a>
      </Dropdown>
    ),
  },
];

/* ─── Results Dashboard: "Type" column support ───────────────────────────────
 *
 * Categories present in app_result_summary, confirmed against live data:
 *   Compare · N/A (Master Suite) · UI Automation · Process Automation
 *
 * Process Automation rows exist (Event Monitor, Extract Compressed File, File Move)
 * but every one carries job_type='MasterSuite-Task', and Results.js filters those out
 * twice — by parentResultSummaryKey and by jobType. They are listed here so the filter
 * is correct if that ever changes, not because they render today.
 */
const RESULT_TYPE_FILTERS = [
  {
    text: labelForCategory("Compare"),
    value: "compare",
    children: [
      { text: labelForSubCategory("Database to Database"), value: "Database to Database" },
      { text: labelForSubCategory("File to Database"), value: "File to Database" },
      { text: labelForSubCategory("File to File"), value: "File to File" },
      { text: labelForSubCategory("File to Specification"), value: "File to Specification" },
    ],
  },
  // Master Suite stores "N/A" in BOTH category and sub-category — it is the stored
  // value, not a gap — so this node has no children to nest under it.
  { text: "Master Suite", value: "n/a" },
  {
    text: "UI Automation",
    value: "ui automation",
    children: [{ text: "UI Test", value: "UI Test" }],
  },
  {
    text: "Process Automation",
    value: "process automation",
    children: [
      { text: "Event Monitor", value: "Event Monitor" },
      { text: "Extract Compressed File", value: "Extract Compressed File" },
      { text: "File Move, Copy or Delete", value: "File Move, Copy or Delete" },
    ],
  },
];

/*
 * Matches a filter value against a row's category OR sub-category.
 *
 * Deliberately lenient, because the stored data is not consistent: runs before late
 * 2024 wrote "Database To Database", "File To Database" and "File To Spec", while
 * everything since writes the lower-case "to" forms. A strict equality check would
 * silently hide 177 legacy rows. The prefix test is what lets the canonical
 * "File to Specification" also match the older, shorter "File To Spec".
 */
const matchesResultType = (value, record) => {
  const v = String(value).toLowerCase();
  const cat = (record.category || "").toLowerCase();
  const sub = (record.subCategory || "").toLowerCase();
  if (cat === v) return true;
  if (!sub) return false;
  return sub === v || sub.startsWith(v) || v.startsWith(sub);
};

/*
 * Master Suite rows store "N/A" as the sub-category. Rendering that literally reads as
 * missing data, so the pill stands alone instead. Same for any row whose sub-category
 * merely repeats its category.
 */
const resultSubCategory = (record) => {
  const sub = record.subCategory;
  if (!sub || sub === "N/A" || sub === record.category) return null;
  return sub;
};

/* ─── Job ID: "On Demand-050926075723657-eb643728" ───────────────────────────
 * The prefix is the trigger type. Confirmed against live data: "On Demand-" (262),
 * "Task-" (107), "Master Suite-" (22), "UI-" (16). Split for reading only — the
 * stored id is never altered, and the column's search filter still matches the whole
 * raw string.
 */
const JOB_ID_TRIGGERS = ["On Demand-", "Master Suite-", "Task-", "UI-"];

const splitJobId = (key) => {
  const raw = key || "";
  const prefix = JOB_ID_TRIGGERS.find((t) => raw.startsWith(t));
  return prefix
    ? { trigger: prefix.slice(0, -1), id: raw.slice(prefix.length) }
    : { trigger: null, id: raw };
};

const copyJobId = (e, value) => {
  // The row itself is clickable, so a copy must not also open the result.
  e.stopPropagation();
  navigator.clipboard?.writeText(value);
  message.success("Job ID copied");
};

/* ─── Parameters ─────────────────────────────────────────────────────────────
 * suiteName packs several values into one string with "<>", "&&" and ";;"
 * separators. Rather than printing every label:value pair inline — which made this
 * the tallest column in the table — the first is shown and the rest move to a
 * tooltip. Nothing is dropped.
 *
 * The label lookup is case-insensitive because the sub-category spellings vary; the
 * previous exact match meant legacy rows got no labels at all.
 */
const PARAM_LABELS = {
  "database to database": ["First Datasource", "Second Datasource"],
  "file to file": ["First File", "Second File"],
  "file to database": ["File Name", "Datasource Name", "Option Name"],
  "file to specification": ["File Name", "Test Suite"],
  "file to spec": ["File Name", "Test Suite"],
  "n/a": ["Master Suite Name"],
};

const parseParameters = (text, record) => {
  const labels = PARAM_LABELS[(record.subCategory || "").toLowerCase()] || [];
  const value = text || "";
  let parts;

  if ((record.subCategory || "").toLowerCase().startsWith("file to spec")) {
    parts = [record.inputName, record.suiteName];
  } else if (value.includes("<>")) {
    const [first, second = ""] = value.split("<>");
    const [middle = "", tail = ""] = second.split("&&");
    const [third = ""] = tail.split(";;");
    parts = [first, middle, third];
  } else {
    parts = [value];
  }

  const pairs = parts
    .map((v, i) => ({ label: labels[i] || "Value", value: (v || "").trim() }))
    .filter((pair) => pair.value);
  return { primary: pairs[0], rest: pairs.slice(1) };
};

export const result_summary_column = [
  {
    title: "Job ID",
    dataIndex: "resultSummaryKey",
    key: "id",
    // Stays 190 even though the cell now needs only ~121px: in grouped mode
    // Results.js renders the GROUP TITLE into this same cell with no colSpan, and
    // the longest of those (a user email, ~185px) only fits on one line at this
    // width. Narrowing the column would wrap every group header.
    width: 190,
    /*
     * Shows the TRIGGER only; the id itself lives on hover.
     *
     * "On Demand-050926075723657-eb643728" was the widest thing in the table and
     * nobody reads it character by character — its leading digits are a timestamp
     * the Run Start column already shows, and the rest is a hash whose only uses
     * are copying it and pasting it into a search. Both still work: the full value
     * is in the tooltip and behind the copy icon. The stored id is unchanged, and
     * this column's search filter still matches the whole raw string.
     */
    render: (text) => {
      const { trigger, id } = splitJobId(text);
      return (
        <Tooltip title={text}>
          <span className="result-jobid-cell">
            <span className="result-jobid-trigger">{trigger || id}</span>
            <CopyOutlined
              className="result-jobid-copy"
              onClick={(e) => copyJobId(e, text)}
            />
          </span>
        </Tooltip>
      );
    },
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      );
    },
    filterIcon: () => {
      return <SearchOutlined style={{ color: "rgb(255,255,255)" }} />;
    },
    onFilter: (value, record) =>
      record.resultSummaryKey.toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: "Parameters",
    dataIndex: "suiteName",
    key: "suiteName",
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      );
    },
    filterIcon: () => {
      return <SearchOutlined style={{ color: "rgb(255,255,255)" }} />;
    },
    onFilter: (value, record) =>
      record.suiteName.toLowerCase().includes(value.toLowerCase()),
    render: (text, record) => {
      const { primary, rest } = parseParameters(text, record);
      if (!primary) return <span className="result-param-muted">—</span>;
      const cell = (
        <div className="result-param-cell">
          <span className="result-param-label">{primary.label}</span>
          <span className="result-param-value">{primary.value}</span>
          {rest.length > 0 && (
            <span className="result-param-more">+{rest.length}</span>
          )}
        </div>
      );
      if (rest.length === 0) return cell;
      return (
        <Tooltip
          title={
            <div className="result-param-tip">
              {[primary, ...rest].map((pair) => (
                <div key={pair.label}>
                  <b>{pair.label}: </b>
                  {pair.value}
                </div>
              ))}
            </div>
          }
        >
          {cell}
        </Tooltip>
      );
    },
  },
  {
    title: "Test Status",
    dataIndex: "testResult",
    key: "testResult",
    filters: [
      {
        text: "Failed",
        value: "Fail",
      },
      {
        text: "Passed",
        value: "Pass",
      },
    ],
    filterMode: "tree",
    onFilter: (value, record) => record.testResult.indexOf(value) === 0,
    render(text) {
      let color = text === "Fail" ? "#ce0000" : "#29b160";
      let data = text === "Fail" ? "Failed" : "Passed";
      return {
        children: (
          <Tag color={color} style={{ fontWeight: "700 !important" }}>
            {data}
          </Tag>
        ),
      };
    },
  },
  {
    // Title and presentation change; the key stays "subCategory" because
    // Results.js groups and wraps columns by that key.
    title: "Type",
    key: "subCategory",
    dataIndex: "subCategory",
    width: 190,
    render: (_, record) => (
      <CategoryTypeCell
        category={record.category}
        subCategory={resultSubCategory(record)}
      />
    ),
    filters: RESULT_TYPE_FILTERS,
    filterMode: "tree",
    onFilter: matchesResultType,
    // Deliberately NO sorter. The column had none before, and the grouped view wraps
    // this key without a group-header guard — adding one would sort headers whose
    // subCategory is undefined. Sorting behaviour is unchanged from before.
  },
  {
    title: "User",
    key: "user",
    dataIndex: "user",
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      );
    },
    filterIcon: () => {
      return <SearchOutlined style={{ color: "rgb(255,255,255)" }} />;
    },
    onFilter: (value, record) =>
      record.user.toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: "Run Start",
    dataIndex: "runStart",
    key: "runStart",
    width: 160,
    defaultSortOrder: "descend",
    sorter: (a, b) =>
      moment(a.runStart, "DD-MM-YYYY HH:mm:ss") -
      moment(b.runStart, "DD-MM-YYYY HH:mm:ss"),
    // One line ("05 Sep 2026, 07:57") instead of a raw two-line timestamp.
    render: (text) => (
      <Tooltip title={text}>
        <span className="result-runstart">{formatDisplayDate(text)}</span>
      </Tooltip>
    ),
  },
  {
    // Replaces "Run End": how long a run took is more useful than when it stopped,
    // and it frees a column. Uses the same formatDuration the Job Details page does.
    title: "Duration",
    key: "duration",
    dataIndex: "runEnd",
    width: 110,
    sorter: (a, b) =>
      parseDateString(a.runEnd) -
      parseDateString(a.runStart) -
      (parseDateString(b.runEnd) - parseDateString(b.runStart)),
    render: (_, record) => {
      const start = parseDateString(record.runStart);
      const end = parseDateString(record.runEnd);
      if (!start || !end || end < start)
        return <span className="result-param-muted">—</span>;
      return (
        <Tooltip title={`Ended ${formatDisplayDate(record.runEnd)}`}>
          <span className="result-duration">{formatDuration(end - start)}</span>
        </Tooltip>
      );
    },
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "action",
  },
];

export const test_case_column = [
  {
    title: "Test Type",
    dataIndex: "testType",
    filters: [
      {
        text: "File Level",
        value: "File Level",
      },
      {
        text: "Column Level",
        value: "Column Level",
      },
    ],
    filterMode: "tree",
    onFilter: (value, record) => record.testType.indexOf(value) === 0,
    sorter: (a, b) => a.testType.length - b.testType.length,
    sortDirections: ["descend"],
    key: "testType",
  },
  {
    title: "Test Name",
    dataIndex: "testName",
    key: "testName",
  },
  {
    title: "Count",
    dataIndex: "totalCount",
    key: "totalCount",
  },
  {
    title: "Column Name",
    dataIndex: "columnName",
    key: "columnName",
    onFilter: (value, record) => record.columnName.indexOf(value) === 0,
  },
  {
    title: "Value",
    dataIndex: "columnValue",
    key: "columnValue",
  },
];

export const test_suite_column = [
  {
    title: "ID",
    dataIndex: "testSuiteId",
  },
  {
    title: "Test Suite Type",
    dataIndex: "testSuiteType",
  },
  {
    title: "Test Suite Name",
    dataIndex: "testSuiteName",
  },
  {
    title: "Control Document",
    dataIndex: "controlDocumentName",
  },
  {
    title: "Label(s)",
    dataIndex: "labels",
  },
  {
    title: "Created By",
    dataIndex: "createdBy",
  },
  {
    title: "Created Date",
    dataIndex: "createdDate",
    key: "createdDate",
    sorter: (a, b) =>
      parseDateString(a.createdDate) - parseDateString(b.createdDate),
    render: (v) => formatDisplayDate(v),
  },
  {
    title: "Comments",
    dataIndex: "comments",
  },
  {
    title: "Tests",
    dataIndex: "testCaseCount",
    render: (text) => (
      <Link>
        {" "}
        <a>{text}</a>
      </Link>
    ),
    onCell: (record, index) => {
      return {
        onClick: () => {
          console.log(record.testSuiteId, index);
        },
      };
    },
  },
  {
    title: "Tasks",
    dataIndex: "taskCount",
  },
  {
    title: "Actions",
  },
];

export const test_result_summary_columns = [
  {
    title: "Category",
    dataIndex: "testType",
    key: "testType",
  },
  {
    title: "Test",
    dataIndex: "testName",
    key: "testName",
  },
  {
    key: "totalCount",
    title: "Total Scenarios",
    dataIndex: "testCount",
  },
  {
    key: "passCount",
    title: "Total Pass",
    dataIndex: "passCount",
  },
  {
    key: "failCount",
    title: "Total Fail",
    dataIndex: "failCount",
  },
  {
    key: "naCount",
    title: "Total N/A",
    dataIndex: "naCount",
  },
  {
    key: "testStatus",
    title: "Test Status",
    dataIndex: "testStatus",
    filters: [
      {
        text: "Failed",
        value: "Fail",
      },
      {
        text: "Passed",
        value: "Pass",
      },
    ],
    filterMode: "tree",
    onFilter: (value, record) => record.testStatus.indexOf(value) === 0,
    render(text) {
      let color = text === "Fail" ? "#ce0000" : "#29b160";
      let data = text === "Fail" ? "Failed" : "Passed";
      return {
        children: <ColorTags color={color} name={data} />,
      };
    },
  },
];

export const task_columns = [
  {
    title: "ID",
    dataIndex: "taskId",
    key: "taskId",
  },
  {
    title: "Category",
    dataIndex: "taskCategory",
    key: "taskCategory",
    render: (v) => labelForCategory(v),
  },
  {
    title: "Sub Category",
    dataIndex: "taskSubCategory",
    key: "taskSubCategory",
    render: (v) => labelForSubCategory(v),
  },
  {
    title: "Test Suite",
    dataIndex: "suiteId",
    key: "suiteId",
  },
];

export const result_fail_row_data = [
  {
    title: "Row #",
    dataIndex: "row",
    key: "row",
  },
  {
    title: "Expected Value",
    dataIndex: "expectedValue",
    key: "expected",
    render: (text) => (
      <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
        {text}
      </div>
    ),
  },
  {
    title: "Actual Value",
    dataIndex: "actualValue",
    key: "actual",
    render: (text) => (
      <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
        {text}
      </div>
    ),
  },

  {
    title: "Raw data",
    dataIndex: "rawData",
    key: "rawData",
    render: (text) => (
      <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
        {text}
      </div>
    ),
  },
];

export const job_queue_summary_column = [
  {
    title: "Job Id",
    dataIndex: "jobId",
    key: "jobId",
  },
  {
    title: "Task Type",
    dataIndex: "taskType",
    key: "taskType",
  },
  {
    title: "Task Category",
    dataIndex: "taskCategory",
    key: "taskCategory",
    render: (v) => labelForCategory(v),
  },
  {
    title: "Task Sub Category",
    dataIndex: "taskSubCategory",
    key: "taskSubCategory",
    render: (v) => labelForSubCategory(v),
  },
  {
    title: "Sequence No",
    dataIndex: "name",
    key: "stepSequence",
    render: (text) => text?.split(".")[0],
    defaultSortOrder: "ascend",
    sorter: (a, b) => {
      const seqA = parseInt(a.name?.split(".")[0] || 0, 10);
      const seqB = parseInt(b.name?.split(".")[0] || 0, 10);
      return seqA - seqB;
    },
  },
  {
    title: "Job Step Name",
    dataIndex: "name",
    key: "name",
    render: (text) => text?.split(".")[1] || text,
  },
  {
    title: "Job Status",
    dataIndex: "jobStatus",
    key: "jobStatus",
    render(text, record) {
      let color;
      switch (text) {
        case "Error":
          color = "#ce0000";
          break;
        case "Completed":
          color = "#29b160";
          break;
        case "In Progress":
          color = "#FFBF00";
          break;
        case "Queued":
          color = "yellow";
          break;
        case "Stopped":
          color = "red";
          break;
        // Fan-out: an upstream step failed, so this job never ran. Neutral, not red —
        // it isn't a failure of its own.
        case "Skipped":
          color = "default";
          break;
        default:
          break;
      }

      return {
        children: (
          <span>
            <Tag color={color} style={{ fontWeight: "700 !important" }}>
              {text}
            </Tag>
            {text === "Error" && (
              <Tooltip title={record.errorDescription}>
                <InfoCircleOutlined
                  style={{ color: "#ce0000", marginLeft: 5 }}
                />
              </Tooltip>
            )}
          </span>
        ),
      };
    },
  },
  {
    title: "Run Start Date",
    dataIndex: "runStart",
    key: "runStart",
    sorter: (a, b) => parseJobDate(a.runStart) - parseJobDate(b.runStart),
    defaultSortOrder: "descend",
  },
  {
    title: "Run End Date",
    dataIndex: "runEnd",
    key: "runEnd",
  },
];

// Parse a `dd-MM-yyyy HH:mm:ss` string to a timestamp; blank/invalid → 0 so the
// comparator never returns NaN (which makes the whole table sort unstable and
// stops the newest row landing on top).
const parseJobDate = (s) => {
  if (!s) return 0;
  const m = /^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/.exec(
    String(s).trim(),
  );
  if (!m) return 0;
  return (
    new Date(`${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}:${m[6]}`).getTime() || 0
  );
};

export const job_queue_summary_column_parent = [
  {
    title: "Job Id",
    dataIndex: "jobId",
    key: "jobId",
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      );
    },
    filterIcon: () => {
      return <SearchOutlined style={{ color: "rgb(255,255,255)" }} />;
    },
    onFilter: (value, record) =>
      record.jobId.toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: "Task Type",
    dataIndex: "taskType",
    key: "taskType",
    filters: [
      {
        text: "Master Suite",
        value: "Master Suite",
      },
      {
        text: "On Demand",
        value: "On Demand",
      },
      {
        text: "UI Automation",
        value: "UI Automation",
      },
    ],
    onFilter: (value, record) => record.taskType === value,
    render: (text) => {
      let color;
      switch (text) {
        case "Master Suite":
          color = "darkblue";
          break;
        case "On Demand":
          color = "#FFBF00";
          break;
        case "UI Automation":
          color = "#722ed1";
          break;
        default:
          break;
      }

      return {
        children: <ColorTags color={color} name={text} />,
      };
    },
  },
  {
    title: "Task Category",
    dataIndex: "taskCategory",
    key: "taskCategory",
    render: (v) => labelForCategory(v),
  },
  {
    title: "Task Sub Category",
    dataIndex: "taskSubCategory",
    key: "taskSubCategory",
    render: (v) => labelForSubCategory(v),
  },
  {
    title: "Job type Name",
    dataIndex: "name",
    key: "name",
    filterDropdown: ({
      selectedKeys,
      setSelectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      );
    },
    filterIcon: () => {
      return <SearchOutlined style={{ color: "rgb(255,255,255)" }} />;
    },
    onFilter: (value, record) =>
      record.name && record.name.toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: "Job Status",
    dataIndex: "jobStatus",
    key: "jobStatus",
    filters: [
      {
        text: "Completed",
        value: "Completed",
      },
      {
        text: "In Progress",
        value: "In Progress",
      },
      {
        text: "Completed with Error",
        value: "Completed with Error",
      },
      {
        text: "Error",
        value: "Error",
      },
    ],
    onFilter: (value, record) => record.jobStatus === value,
    render(text, record) {
      let color;
      switch (text) {
        case "Error":
          color = COLORS.ERROR;
          break;
        case "Completed":
          color = COLORS.COMPLETED;
          break;
        case "In Progress":
          color = COLORS.IN_PROGRESS;
          break;
        case "Queued":
          color = COLORS.QUEUED;
          break;
        case "Completed with Error":
          color = COLORS.WITH_ERROR;
          break;
        default:
          break;
      }

      return {
        children: (
          <span>
            <Tag color={color} style={{ fontWeight: "700 !important" }}>
              {text}
            </Tag>
            {text === "Error" && (
              <Tooltip title={record.errorDescription}>
                <InfoCircleOutlined
                  style={{ color: "#ce0000", marginLeft: 5 }}
                />
              </Tooltip>
            )}
          </span>
        ),
      };
    },
  },
  {
    title: "Run Start Date",
    dataIndex: "runStart",
    key: "runStart",
    sorter: (a, b) => parseJobDate(a.runStart) - parseJobDate(b.runStart),
    defaultSortOrder: "descend",
  },
  {
    title: "Run End Date",
    dataIndex: "runEnd",
    key: "runEnd",
    render: (text, record) => {
      return record.taskType === "Master Suite"
        ? record.runEnd
        : record.runStart;
    },
  },
];

// Tree filter: categories as parents, their sub-categories as children
const TASK_TYPE_FILTERS = [
  {
    // Display text via the shared label map; filter `value`s stay raw (data).
    text: labelForCategory("Compare"),
    value: "compare",
    children: [
      {
        text: labelForSubCategory("Database to Database"),
        value: "Database to Database",
      },
      { text: labelForSubCategory("File to Database"), value: "File to Database" },
      { text: labelForSubCategory("File to File"), value: "File to File" },
      {
        text: labelForSubCategory("File to Specification"),
        value: "File to Specification",
      },
    ],
  },
  {
    text: "Process Automation",
    value: "process automation",
    children: [
      { text: "Event Monitor", value: "Event Monitor" },
      { text: "Extract Compressed File", value: "Extract Compressed File" },
      {
        text: "File Move, Copy or Delete",
        value: "File Move, Copy or Delete",
      },
    ],
  },
];

export const task_column = [
  {
    title: "Task Id",
    dataIndex: "taskId",
    key: "taskId",
    sorter: (a, b) =>
      a.taskId.localeCompare(b.taskId, undefined, { numeric: true }),
  },
  {
    title: "Task Name",
    dataIndex: "taskName",
    key: "taskName",
    filterDropdown: ({
      selectedKeys,
      setSelectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      );
    },
    filterIcon: () => {
      return <SearchOutlined style={{ color: "rgb(255,255,255)" }} />;
    },
    onFilter: (value, record) =>
      record.taskName.toLowerCase().includes(value.toLowerCase()),
    sorter: (a, b) => a.taskName.localeCompare(b.taskName),
    render: (text) => <span className="arc-library-name">{text}</span>,
  },
  {
    title: "Type",
    dataIndex: "taskCategory",
    key: "taskCategory",
    sorter: (a, b) =>
      (a.taskCategory || "").localeCompare(b.taskCategory || "") ||
      (a.taskSubCategory || "").localeCompare(b.taskSubCategory || ""),
    filters: TASK_TYPE_FILTERS,
    filterMode: "tree",
    onFilter: (value, record) =>
      record.taskCategory?.toLowerCase() === String(value).toLowerCase() ||
      record.taskSubCategory === value,
    render: (category, record) => (
      <CategoryTypeCell category={category} subCategory={record.taskSubCategory} />
    ),
  },
  {
    title: "Test Suite",
    dataIndex: "testSuiteId",
    key: "testSuiteId",
    filterDropdown: ({
      selectedKeys,
      setSelectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      );
    },
    filterIcon: () => {
      return <SearchOutlined style={{ color: "rgb(255,255,255)" }} />;
    },
    onFilter: (value, record) =>
      record.testSuiteId &&
      record.testSuiteId.toLowerCase().includes(value.toLowerCase()),
    sorter: (a, b) =>
      a.testSuiteId.localeCompare(b.testSuiteId, undefined, { numeric: true }),
  },
  {
    title: "Label(s)",
    dataIndex: "label",
    key: "label",
  },
  {
    title: "Created By",
    dataIndex: "createdBy",
    key: "createdBy",
    filterDropdown: ({
      selectedKeys,
      setSelectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      );
    },
    filterIcon: () => {
      return <SearchOutlined style={{ color: "rgb(255,255,255)" }} />;
    },
    onFilter: (value, record) =>
      record.createdBy.toLowerCase().includes(value.toLowerCase()),
    sorter: (a, b) => a.createdBy.localeCompare(b.createdBy),
  },
  {
    title: "Created Date",
    dataIndex: "createdDate",
    key: "createdDate",
    defaultSortOrder: "descend",
    sorter: (a, b) =>
      parseDateString(a.createdDate) - parseDateString(b.createdDate),
    render: (v) => formatDisplayDate(v),
  },
  {
    title: "Comments",
    dataIndex: "comments",
    key: "comments",
    filterDropdown: ({
      selectedKeys,
      setSelectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      );
    },
    filterIcon: () => {
      return <SearchOutlined style={{ color: "rgb(255,255,255)" }} />;
    },
    onFilter: (value, record) =>
      record.comments.toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: "Options",
    key: "options",
    render: (_, record) => (
      <Dropdown
        menu={{
          items: [
            {
              key: "1",
              label: "Run Task",
            },
            {
              key: "2",
              label: "View",
            },
          ],
        }}
        overlayClassName="table-row-actions-dropdown"
      >
        <a className="table-more-options">
          Options <DownCircleFilled />
        </a>
      </Dropdown>
    ),
  },
];
export const db_library_column = [
  {
    title: "Display Name",
    dataIndex: "displayName",
    key: "displayName",
  },
  {
    title: "Database Type",
    dataIndex: "databaseType",
    key: "databaseType",
  },
  {
    title: "Host Name",
    dataIndex: "hostName",
    key: "hostName",
  },
  {
    title: "Port",
    dataIndex: "port",
    key: "port",
  },
  {
    title: "Created Date",
    dataIndex: "createDate",
    key: "createDate",
    sorter: (a, b) =>
      parseDateString(a.createDate) - parseDateString(b.createDate),
    render: (v) => formatDisplayDate(v),
  },
  {
    title: "Environment",
    dataIndex: "environment",
    key: "environment",
  },
  {
    title: "Options",
    key: "options",
    render: (_, record) => (
      <Dropdown
        menu={{
          items: [
            {
              key: "1",
              label: "Edit",
            },
            {
              key: "2",
              label: "Test Connection",
            },
          ],
        }}
        overlayClassName="table-row-actions-dropdown"
      >
        <a className="table-more-options">
          Options <DownCircleFilled />
        </a>
      </Dropdown>
    ),
  },
];

// Created dates are stored as "dd-MM-yyyy HH:mm:ss" strings — parse to a timestamp
// so the column sorts chronologically (text sort would be wrong); empty dates sort last.
export const environment_library_column = [
  {
    title: "Display Name",
    dataIndex: "displayName",
    key: "displayName",
    filterDropdown: ({
      selectedKeys,
      setSelectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <SearchFilter
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      );
    },
    filterIcon: () => {
      return <SearchOutlined style={{ color: "rgb(255,255,255)" }} />;
    },
    onFilter: (value, record) =>
      record.displayName.toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: "# Folder Paths",
    dataIndex: "folderCount",
    key: "folderCount",
  },
  {
    title: "# Database Connections",
    dataIndex: "databaseCount",
    key: "databaseCount",
  },
  {
    title: "Created Date",
    dataIndex: "createdDate",
    key: "createdDate",
    defaultSortOrder: "descend",
    sorter: (a, b) =>
      parseDateString(a.createdDate) - parseDateString(b.createdDate),
    render: (v) => formatDisplayDate(v),
  },
  {
    title: "Options",
    key: "options",
    render: null,
  },
];

export const createMasterSuiteResultDetailColumns = (clickTableRow) => [
  {
    title: "Category",
    dataIndex: "category",
    key: "category",
    render: (v) => labelForCategory(v),
  },
  {
    title: "Sub Category",
    dataIndex: "subCategory",
    key: "subCategory",
    render: (v) => labelForSubCategory(v),
  },
  {
    title: "Sequence No",
    dataIndex: "stepSequenceNumber",
    key: "stepSequenceNumber",
    defaultSortOrder: "ascend",
    sorter: (a, b) => a.stepSequenceNumber - b.stepSequenceNumber,
  },
  {
    title: "Step Name",
    dataIndex: "stepName",
    key: "stepName",
  },
  {
    title: "Task Name",
    dataIndex: "taskName",
    key: "taskName",
    render: (text, record) => (
      <a
        href={`/results/details?key=${record.stepResultId}`}
        onClick={(e) => {
          e.preventDefault();
          clickTableRow(record);
        }}
      >
        {record.taskName}
      </a>
    ),
  },
  {
    title: "Stop If Failed",
    dataIndex: "stopIfFailed",
    key: "stopIfFailed",
    render: (_, record) => (
      <Switch
        className="custom-switch"
        checked={record.stopIfFailed}
        checkedChildren="ON"
        unCheckedChildren="OFF"
      />
    ),
    align: "center",
  },
  {
    title: "Total Scenarios",
    dataIndex: "totalTestCount",
    key: "totalTestCount",
  },
  {
    title: "Total Pass",
    dataIndex: "passedTestCount",
    key: "passedTestCount",
  },
  {
    title: "Total Fail",
    dataIndex: "failedTestCount",
    key: "failedTestCount",
  },
  {
    title: "Total N/A",
    dataIndex: "totalNaCount",
    key: "totalNaCount",
  },
  {
    title: "Test Status",
    dataIndex: "stepTestResult",
    key: "stepTestStatus",
    render(text) {
      const status =
        text === null || text === undefined
          ? "Fail"
          : text === "Stopped"
            ? "Stopped"
            : text;
      let color =
        status === "Fail"
          ? "#ce0000"
          : status === "Error" || status === "Partial"
            ? COLORS.WITH_ERROR
            : status === "Pass"
              ? "#29b160"
              : "#808080";   // Skipped / Stopped / anything unknown → neutral
      let data =
        status === "Fail"
          ? "Failed"
          : status === "Stopped"
            ? "Stopped"
            : "Passed";

      return {
        children: <ColorTags color={color} name={status} />,
      };
    },
  },
];
